import { swiperSlider, debounce, smoothSwiperOverrides } from '@theme/coco-utilities';

if (!customElements.get('component-slider')) {
  class ComponentSlider extends HTMLElement {
    constructor() {
      super();
      this.sliderInstance = null;
      this.slider = null;
      this.params = {};
      this._unbindTextAnimate = null;
      this.mediaQuery = window.matchMedia('(min-width: 768px)');
      this.onResize = debounce(this.handleSlider.bind(this), 150);
    }

    connectedCallback() {
      this.section = this.closest('.shopify-section');
      this.readSliderRefs();

      if (!this.slider) return;
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.slider.style.setProperty(
          '--swiper-wrapper-transition-timing-function',
          'cubic-bezier(0.16, 1, 0.3, 1)'
        );
      }
      this.mediaQuery.addEventListener('change', this.onResize);
      this._onRecommendationsLoaded = this.onRecommendationsLoaded.bind(this);
      document.addEventListener('recommendations:loaded', this._onRecommendationsLoaded);
      document.addEventListener('recentlyViewed:loaded', this._onRecommendationsLoaded);
      this.handleSlider();
    }

    disconnectedCallback() {
      this.mediaQuery.removeEventListener('change', this.onResize);
      document.removeEventListener('recommendations:loaded', this._onRecommendationsLoaded);
      document.removeEventListener('recentlyViewed:loaded', this._onRecommendationsLoaded);
      this.cleanupSlider();
    }

    readSliderRefs() {
      this.slider =
        (this.hasAttribute('data-slider') ? this : null) ?? this.querySelector('[data-slider]');
      this.params = this.slider?.dataset.slider
        ? JSON.parse(this.slider.dataset.slider)
        : {};
    }

    onRecommendationsLoaded(event) {
      // Event is dispatched on coco-recs (parent), not a child
      if (!this.isConnected || !event.target?.contains?.(this)) return;
      requestAnimationFrame(() => this.refreshSlider());
    }

    refreshSlider() {
      this.readSliderRefs();

      const hasSlides = this.slider?.querySelector('.swiper-slide, [data-swiper-slide]');
      if (!this.slider || !hasSlides) return;

      if (this.sliderInstance && !this.sliderInstance.destroyed) {
        this.sliderInstance.update();
        this.expandableProducts(this);
        return;
      }

      // Slides arrived after AJAX — init immediately (skip viewport wait)
      const sliderOpts = {
        ...this.params,
        ...smoothSwiperOverrides(this.params),
        defer: false,
        onReady: (instance) => {
          this.sliderInstance = instance;
          this.expandableProducts(this);
          this.bindTextAnimate();
        },
      };
      this.sliderInstance = swiperSlider(this.slider, sliderOpts);
    }

    handleSlider() {
      if (!this.sliderInstance) this.init();
    }


    ensureTextAnimate() {
      if (typeof TextAnimate === 'undefined') return null;
      if (!(window.textAnimate instanceof TextAnimate)) {
        window.textAnimate = new TextAnimate();
      }
      return window.textAnimate;
    }

    bindTextAnimate() {
      if (this.slider?.dataset.textAnimateOn !== 'change') return;

      const attach = () => {
        if (!this.isConnected) return true;
        const swiper = this.sliderInstance;
        if (!swiper || swiper.destroyed) return false;

        const textAnimate = this.ensureTextAnimate();
        if (!textAnimate) return false;

        this._unbindTextAnimate?.();
        const delay = Number(this.slider.dataset.textAnimateDelay);
        const delayMs = Number.isFinite(delay) && delay >= 0 ? delay : 650;
        this._unbindTextAnimate = textAnimate.bindSwiper(swiper, {
          events: ['slideChangeTransitionStart'],
          delay: delayMs,
        });
        return true;
      };

      if (attach()) return;

      let attempts = 0;
      const retry = () => {
        if (!this.isConnected || attempts > 60) return;
        attempts += 1;
        if (attach()) return;
        requestAnimationFrame(retry);
      };
      requestAnimationFrame(retry);
    }

    unbindTextAnimate() {
      this._unbindTextAnimate?.();
      this._unbindTextAnimate = null;
    }


    init() {
      const hasSlides = this.slider?.querySelector('.swiper-slide, [data-swiper-slide]');
      if (!hasSlides) return;

      const sliderOpts = {
        ...this.params,
        ...smoothSwiperOverrides(this.params),
        onReady: (instance) => {
          this.sliderInstance = instance;
          this.expandableProducts(this);
          this.bindTextAnimate();
        },
      };
      // Auto: section 1–2 eager; section 3+ when near viewport (coco-utilities)
      this.sliderInstance = swiperSlider(this.slider, sliderOpts);
    }

    cleanupSlider() {
      this.unbindTextAnimate();
      if (this.sliderInstance && !this.sliderInstance.destroyed) {
        this.sliderInstance.destroy(true, true);
      }

      this.sliderInstance = null;
    }

    expandableProducts(element) {
      const syncCardFace = (card, { focusTarget } = {}) => {
        if (!card) return;

        const isOpen = card.classList.contains('active');
        const front = card.querySelector('[data-shopable-card-front]');
        const back = card.querySelector('[data-shopable-card-back]');
        const frontTrigger =
          front?.querySelector('button, a, [tabindex]:not([tabindex^="-"])') || front;

        if (back) {
          back.toggleAttribute('inert', !isOpen);
          back.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
        }

        if (front) {
          // While the back face is open, keep the front trigger out of tab order.
          front.toggleAttribute('inert', isOpen);
          frontTrigger?.setAttribute?.('aria-expanded', isOpen ? 'true' : 'false');
        }

        if (focusTarget === 'back') {
          // Start keyboard focus on the product list, not the close button.
          const firstProduct =
            back?.querySelector(
              '.shopable-card-products a[href], .shopable-card-products button:not([disabled])'
            ) ||
            back?.querySelector(
              'a[href]:not([data-shopable-card-close]), button:not([disabled]):not([data-shopable-card-close])'
            );
          firstProduct?.focus?.({ preventScroll: true });
        } else if (focusTarget === 'front') {
          frontTrigger?.focus?.({ preventScroll: true });
        }
      };

      const setCardOpen = (card, open, { focus } = {}) => {
        if (!card) return;

        if (open) {
          this.querySelectorAll('[data-shopable-card].active').forEach((other) => {
            if (other === card) return;
            other.classList.remove('active');
            syncCardFace(other);
          });
          card.classList.add('active');
          syncCardFace(card, { focusTarget: focus ? 'back' : null });
        } else {
          card.classList.remove('active');
          syncCardFace(card, { focusTarget: focus ? 'front' : null });
        }
      };

      // Closed backs must not receive Tab until the front CTA opens them.
      this.querySelectorAll('[data-shopable-card]').forEach((card) => {
        const frontTrigger =
          card.querySelector('[data-shopable-card-front] button, [data-shopable-card-front] a') ||
          card.querySelector('[data-shopable-card-front]');
        frontTrigger?.setAttribute?.('aria-expanded', 'false');
        syncCardFace(card);
      });

      this.querySelectorAll('[data-shopable-card-close]').forEach((closeBtn) => {
        closeBtn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const card = closeBtn.closest('[data-shopable-card]');
          setCardOpen(card, false, { focus: true });
        });
      });

      this.querySelectorAll('[data-shopable-card-front]').forEach((el) => {
        el.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const currentCard = el.closest('[data-shopable-card]');
          if (!currentCard) return;
          setCardOpen(currentCard, !currentCard.classList.contains('active'), { focus: true });
        });
      });

      // Clicks on the open back (outside links/buttons) should not steal focus
      // from product cards; only the close control closes it.
    }
  }

  customElements.define('component-slider', ComponentSlider);
}
